import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function Dialog({
  title,
  children,
  onClose,
  drawer = false,
  initialFocus,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  drawer?: boolean;
  initialFocus?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const id = useId();
  const [viewport, setViewport] = useState<{ height: number; top: number } | null>(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setViewport({ height: vv.height, top: vv.offsetTop });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const root = ref.current!;
    const app = document.getElementById('root');
    const wasInert = app?.inert ?? false;
    if (app) app.inert = true;
    const focusable = () =>
      [
        ...root.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), textarea, select, a[href], [tabindex="0"]',
        ),
      ].filter((el) => el.getClientRects().length);
    (initialFocus
      ? (root.querySelector<HTMLElement>(initialFocus) ?? focusable()[0] ?? root)
      : (focusable()[0] ?? root)
    ).focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        close.current();
      }
      if (event.key === 'Tab') {
        const nodes = focusable();
        const first = nodes[0];
        const last = nodes.at(-1);
        if (!first) {
          event.preventDefault();
          root.focus();
        } else if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('keydown', key, true);
      if (app) app.inert = wasInert;
      (previous?.isConnected
        ? previous
        : (document.querySelector<HTMLElement>('[aria-label="Board actions"]') ??
          document.querySelector<HTMLElement>('[role=application]'))
      )?.focus();
    };
  }, []);
  return createPortal(
    <div
      className="sp-backdrop"
      style={viewport ? { height: viewport.height, top: viewport.top, bottom: 'auto' } : undefined}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        onPointerUp={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        className={`sp-dialog ${drawer ? 'sp-drawer' : ''}`}
      >
        <header className="sp-dialog-heading">
          <h2 id={id}>{title}</h2>
          <button className="sp-icon" aria-label={`Close ${title}`} onClick={onClose}>
            ×
          </button>
        </header>
        <div className="sp-dialog-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      title={title}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <p className="sp-description">{description}</p>
      {error && (
        <p role="alert" className="sp-error">
          {error}
        </p>
      )}
      <div className="sp-dialog-actions">
        <button className="sp-button" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        <button
          className="sp-button sp-danger"
          disabled={busy}
          aria-busy={busy}
          onClick={async () => {
            if (busy) return;
            setBusy(true);
            setError(null);
            try {
              await onConfirm();
              onClose();
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Please try again.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
